// src/services/fishIQWaterContext.ts
// Shared FishIQ water-context helper.
// Keep this file pure: no network calls, no Firestore, no Map side effects.

export type FishIQWaterContextInput = {
  lat?: number | null;
  lon?: number | null;
  waterName?: string | null;
  waterKind?: string | null;
  waterType?: string | null;
  regionHint?: string | null;
};

export type FishIQWaterContextClass =
  | 'freshwater'
  | 'inland'
  | 'coastal'
  | 'offshore'
  | 'unknown';

export type FishIQResolvedWaterContext = {
  contextClass: FishIQWaterContextClass;
  freshwaterSignal: boolean;
  coastalSignal: boolean;
  offshoreSignal: boolean;
  nearCoastSignal: boolean;
  knownFreshwaterAnchor: boolean;
  knownCoastalAnchor: boolean;
  haystack: string;
  reason: string;
};

const clean = (value: any) => String(value ?? '').trim();
const lower = (value: any) => clean(value).toLowerCase();

export const FISHIQ_FRESHWATER_RE =
  /\b(lake|reservoir|pond|river|creek|stream|brook|canal|ditch|drain|freshwater|fresh water|waterway|inland|dam|basin|wetland)\b/;

export const FISHIQ_COASTAL_RE =
  /\b(bay|estuary|inshore|coast|coastal|harbor|harbour|marina|sound|inlet|lagoon|delta|tidal|brackish|saltwater|salt water|beach|surf|pier|jetty|breakwater|shore|strait|gulf|mangrove|port|nearshore|island nearshore|reef|coral|backwater)\b/;

export const FISHIQ_OFFSHORE_RE =
  /\b(ocean|sea|offshore|gulf|pacific|atlantic|marine)\b/;


export const FISHIQ_BRACKISH_RE =
  /\b(brackish|estuary|estuarine|lagoon|delta|tidal|salt marsh|coastal river|river mouth|mouth of the river|backwater|backwaters)\b/;

// Stronger than a generic river/creek token. These terms indicate actual tidal
// mixing water and should not be forced freshwater simply because "river" also
// appears in the name. Plain "delta"/"lagoon" remain handled by the broader
// coastal signal because they can be ambiguous in some datasets.
const FISHIQ_STRONG_BRACKISH_RE =
  /\b(brackish|estuary|estuarine|tidal river|tidal creek|salt marsh|coastal river|river mouth|mouth of the river|backwater|backwaters)\b/;

const FISHIQ_FRESHWATER_HABITAT_RE =
  /\b(lake|reservoir|pond|river|creek|stream|brook|canal|ditch|drain|freshwater|fresh water|waterway|inland|dam|basin|wetland|slough|riffle)\b/;

const FISHIQ_COASTAL_HABITAT_RE =
  /\b(ocean|sea|offshore|nearshore|inshore|coast|coastal|marine|reef|kelp|surf|beach|pier|jetty|breakwater|harbor|harbour|marina|bay|sound|inlet|strait|gulf|mangrove|saltwater|salt water|backwater|reef|coral)\b/;

const list = (value: any): any[] => (Array.isArray(value) ? value : value == null ? [] : [value]);

const speciesHabitatHaystack = (species: any): string =>
  [
    species?.category,
    ...(list(species?.waterRealms)),
    ...(list(species?.habitats)),
    ...(list(species?.regions)),
    ...(list(species?.behaviorTags)),
    ...(list(species?.structureTags)),
    species?.summary,
    species?.description,
  ]
    .map(lower)
    .filter(Boolean)
    .join(' ');

export function fishIQSpeciesAllowedForWaterContext(
  species: any,
  ctx: FishIQWaterContextInput,
): boolean {
  const resolved = resolveWaterContext(ctx);
  const category = lower(species?.category);
  const realms = list(species?.waterRealms).map(lower);
  const speciesHay = speciesHabitatHaystack(species);

  const freshwaterCapable =
    category === 'freshwater' ||
    realms.includes('freshwater') ||
    FISHIQ_FRESHWATER_HABITAT_RE.test(speciesHay);

  const coastalCapable =
    category === 'saltwater' ||
    realms.includes('saltwater') ||
    realms.includes('marine') ||
    FISHIQ_COASTAL_HABITAT_RE.test(speciesHay);

  const brackishCapable =
    realms.includes('brackish') ||
    FISHIQ_BRACKISH_RE.test(speciesHay) ||
    /\b(estuary|bay|inshore|river mouth|tidal river)\b/.test(speciesHay);

  const freshwaterOnly = freshwaterCapable && !coastalCapable && !brackishCapable;
  const coastalOnly = coastalCapable && !freshwaterCapable && !brackishCapable;
  const brackishSupported = FISHIQ_BRACKISH_RE.test(resolved.haystack);

  if (resolved.contextClass === 'freshwater' || resolved.contextClass === 'inland') {
    if (coastalOnly) return false;
    if (category === 'saltwater' && !brackishCapable) return false;
    if (brackishCapable && !freshwaterCapable && !brackishSupported) return false;
    return freshwaterCapable || (brackishSupported && brackishCapable);
  }

  if (resolved.contextClass === 'coastal' || resolved.contextClass === 'offshore') {
    if (freshwaterOnly) return false;
    return coastalCapable || brackishCapable;
  }

  // Unknown map/water context should stay launch-safe: allow true freshwater,
  // anadromous/brackish bridge species, and fully shared species, but do not
  // let coastal-only regional fallback flood an inland read.
  if (coastalOnly) return false;
  if (category === 'saltwater' && !brackishCapable) return false;
  return freshwaterCapable || brackishCapable || category === 'both' || category !== 'saltwater';
}

const KNOWN_FRESHWATER_ANCHOR_RE =
  /\b(laguna de bay|tonle sap|mekong|red river|chao phraya|pasig river|pampanga river|cagayan river|pahang river|perak river|kinabatangan|rajang river|kapuas|mahakam|musi river|toba|maninjau|singkarak|tempe lake|jatiluhur|cirata|saguling|rinjani|sentarum|marina reservoir|macritchie|bedok reservoir|kallang river|singapore river)\b/;

const KNOWN_COASTAL_ANCHOR_RE =
  /\b(south china sea|java sea|andaman sea|philippine sea|sulu sea|celebes sea|bali sea|flores sea|banda sea|malacca strait|singapore strait|gulf of thailand|tokyo bay|osaka bay|manila bay|ha long bay|nha trang bay|jakarta bay|kerala backwaters|arabian sea|bay of bengal)\b/;

export function fishIQWaterHaystack(ctx: FishIQWaterContextInput): string {
  return [ctx.regionHint, ctx.waterName, ctx.waterKind, ctx.waterType]
    .map(lower)
    .filter(Boolean)
    .join(' ');
}

export function hasFreshwaterSignal(ctx: FishIQWaterContextInput | string): boolean {
  const hay = typeof ctx === 'string' ? lower(ctx) : fishIQWaterHaystack(ctx);
  return FISHIQ_FRESHWATER_RE.test(hay) || KNOWN_FRESHWATER_ANCHOR_RE.test(hay);
}

export function hasCoastalSignal(ctx: FishIQWaterContextInput | string): boolean {
  const hay = typeof ctx === 'string' ? lower(ctx) : fishIQWaterHaystack(ctx);
  return FISHIQ_COASTAL_RE.test(hay) || KNOWN_COASTAL_ANCHOR_RE.test(hay);
}

export function hasOffshoreSignal(ctx: FishIQWaterContextInput | string): boolean {
  const hay = typeof ctx === 'string' ? lower(ctx) : fishIQWaterHaystack(ctx);
  return FISHIQ_OFFSHORE_RE.test(hay);
}

export function isKnownFreshwaterAnchor(ctx: FishIQWaterContextInput | string): boolean {
  const hay = typeof ctx === 'string' ? lower(ctx) : fishIQWaterHaystack(ctx);
  return KNOWN_FRESHWATER_ANCHOR_RE.test(hay);
}

export function isKnownCoastalAnchor(ctx: FishIQWaterContextInput | string): boolean {
  const hay = typeof ctx === 'string' ? lower(ctx) : fishIQWaterHaystack(ctx);
  return KNOWN_COASTAL_ANCHOR_RE.test(hay);
}

export function isNearFishIQCoastlineByCoords(ctx: FishIQWaterContextInput): boolean {
  const lat = Number(ctx.lat);
  const lon = Number(ctx.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;

  // SoCal needs a tighter coastal strip. The old broad box reached too far inland
// and let surf/bay/ocean species appear around Inland Empire / inland SoCal.
// Explicit freshwater names still win before this fallback.
const nearSoCalCoast =
  // San Diego coastal strip
  (lat >= 32.45 && lat <= 33.25 && lon >= -117.45 && lon <= -117.05) ||
  // Orange County coastal strip
  (lat > 33.25 && lat <= 33.8 && lon >= -118.15 && lon <= -117.65) ||
  // LA / South Bay / Long Beach coastal strip
  (lat > 33.7 && lat <= 33.95 && lon >= -118.65 && lon <= -118.05) ||
  // Malibu / Ventura coastal strip
  (lat > 33.95 && lat <= 34.45 && lon >= -119.45 && lon <= -118.45);

const nearLaOcVenturaCoast = false;
  const nearCentralNorCalCoast = lat > 34.55 && lat <= 42.2 && lon >= -124.8 && lon <= -121.45;
  const nearPacificNorthwestCoast = lat >= 42.0 && lat <= 49.2 && lon >= -125.5 && lon <= -123.0;
  const nearAtlanticCoast = lat >= 30.0 && lat <= 47.8 && lon >= -80.35 && lon <= -66.0;
  const nearGulfCoast = lat >= 25.0 && lat <= 30.65 && lon >= -98.2 && lon <= -82.7;
  const nearFloridaCoast = lat >= 24.0 && lat <= 31.2 && lon >= -87.8 && lon <= -79.6 && (lon >= -80.55 || lon <= -82.55 || lat <= 25.7);
  const nearAlaskaCoast = lat >= 51.0 && lat <= 72.5 && lon >= -170.5 && lon <= -129.0 && (lat <= 61.8 || lon <= -147.0 || lon >= -134.0);
  const nearHawaiiCoast = lat >= 18.5 && lat <= 22.8 && lon >= -161.0 && lon <= -154.5;

  const nearVietnamCoast = lat >= 8.0 && lat <= 23.7 && lon >= 102.0 && lon <= 109.8 && (lon >= 105.0 || lat <= 11.2);
  const nearSingaporeCoast = lat >= 1.12 && lat <= 1.55 && lon >= 103.55 && lon <= 104.15;
  const nearMalaysiaCoast = lat >= 0.8 && lat <= 7.8 && lon >= 99.0 && lon <= 119.5 && (lon <= 100.8 || (lon >= 102.4 && lon <= 104.8) || lon >= 109.0 || lat <= 2.1);
  const nearThailandCoast = lat >= 5.4 && lat <= 13.8 && lon >= 97.0 && lon <= 106.2 && (lon <= 99.3 || lon >= 100.7 || lat <= 10.5);
  const nearPhilippinesCoast = lat >= 4.0 && lat <= 21.5 && lon >= 116.0 && lon <= 127.5 && (lat <= 7.0 || lat >= 18.0 || lon <= 120.1 || lon >= 124.2);
  const nearIndonesiaCoast = lat >= -11.5 && lat <= 6.5 && lon >= 94.0 && lon <= 142.5 && (lat <= -8.2 || lat >= 3.8 || lon <= 98.0 || lon >= 138.0);
  const nearJapanCoast = lat >= 24.0 && lat <= 46.5 && lon >= 122.0 && lon <= 146.5 && (lat <= 31.0 || lat >= 43.0 || lon <= 131.0 || lon >= 140.0);
  // India needs a deliberately narrow coordinate-only fallback. The previous
  // country-sized west/east longitude test classified large inland areas (for
  // example Bengaluru/Karnataka) as coastal whenever no water label was ready.
  // Explicit names such as Arabian Sea, Bay of Bengal, estuary, reef, etc. are
  // still handled above by text signals; these strips are only a last resort.
  const nearIndiaWestCoast =
    (lat >= 6.0 && lat < 10.5 && lon >= 74.0 && lon <= 77.2) ||
    (lat >= 10.5 && lat < 16.5 && lon >= 72.5 && lon <= 75.8) ||
    (lat >= 16.5 && lat < 21.5 && lon >= 72.0 && lon <= 74.8) ||
    (lat >= 21.5 && lat <= 24.5 && lon >= 68.0 && lon <= 73.6);
  const nearIndiaEastCoast =
    (lat >= 8.0 && lat < 14.5 && lon >= 79.4 && lon <= 81.0) ||
    (lat >= 14.5 && lat < 19.5 && lon >= 80.5 && lon <= 85.0) ||
    (lat >= 19.5 && lat < 22.5 && lon >= 84.5 && lon <= 88.5) ||
    (lat >= 22.5 && lat <= 24.5 && lon >= 87.0 && lon <= 90.5);
  const nearIndiaCoast = nearIndiaWestCoast || nearIndiaEastCoast;

  return Boolean(
    nearSoCalCoast ||
      nearLaOcVenturaCoast ||
      nearCentralNorCalCoast ||
      nearPacificNorthwestCoast ||
      nearAtlanticCoast ||
      nearGulfCoast ||
      nearFloridaCoast ||
      nearAlaskaCoast ||
      nearHawaiiCoast ||
      nearVietnamCoast ||
      nearSingaporeCoast ||
      nearMalaysiaCoast ||
      nearThailandCoast ||
      nearPhilippinesCoast ||
      nearIndonesiaCoast ||
      nearJapanCoast ||
      nearIndiaCoast,
  );
}

export function classifyWaterContext(ctx: FishIQWaterContextInput): FishIQWaterContextClass {
  const resolved = resolveWaterContext(ctx);
  return resolved.contextClass;
}

export function maybeClassifyWaterContext(ctx: FishIQWaterContextInput): FishIQWaterContextClass | null {
  const resolved = resolveWaterContext(ctx);
  return resolved.contextClass === 'unknown' ? null : resolved.contextClass;
}

export function resolveWaterContext(ctx: FishIQWaterContextInput): FishIQResolvedWaterContext {
  const haystack = fishIQWaterHaystack(ctx);
  const knownFreshwaterAnchor = isKnownFreshwaterAnchor(haystack);
  const knownCoastalAnchor = isKnownCoastalAnchor(haystack);
  const freshwaterSignal = hasFreshwaterSignal(haystack);
  const offshoreSignal = hasOffshoreSignal(haystack);
  const coastalSignal = hasCoastalSignal(haystack);
  const strongBrackishSignal = FISHIQ_STRONG_BRACKISH_RE.test(haystack);
  const explicitFreshwaterKind = /^(freshwater|lake|reservoir|pond)$/i.test(clean(ctx.waterKind)) ||
    /^(freshwater|lake|reservoir|pond)$/i.test(clean(ctx.waterType));
  const nearCoastSignal = !freshwaterSignal && isNearFishIQCoastlineByCoords(ctx);

  // Explicit tidal/estuarine language is stronger than the generic word
  // "river". Keep a caller's explicit freshwater kind authoritative, but let
  // "Tijuana River Estuary" / "tidal creek" resolve coastal rather than inland.
  if (strongBrackishSignal && !explicitFreshwaterKind) {
    return {
      contextClass: 'coastal',
      freshwaterSignal,
      coastalSignal: true,
      offshoreSignal,
      nearCoastSignal,
      knownFreshwaterAnchor,
      knownCoastalAnchor,
      haystack,
      reason: 'explicit brackish/tidal water signal',
    };
  }

  // Locked rule: explicit freshwater/inland wins before broad coastal words.
  // Example: Laguna de Bay, reservoirs, canals, dams, wetlands, ordinary rivers.
  if (freshwaterSignal) {
    return {
      contextClass: 'freshwater',
      freshwaterSignal,
      coastalSignal,
      offshoreSignal,
      nearCoastSignal: false,
      knownFreshwaterAnchor,
      knownCoastalAnchor,
      haystack,
      reason: knownFreshwaterAnchor ? 'known freshwater anchor' : 'freshwater text signal',
    };
  }

  if (offshoreSignal) {
    return {
      contextClass: 'offshore',
      freshwaterSignal,
      coastalSignal,
      offshoreSignal,
      nearCoastSignal,
      knownFreshwaterAnchor,
      knownCoastalAnchor,
      haystack,
      reason: 'offshore/ocean text signal',
    };
  }

  if (coastalSignal) {
    return {
      contextClass: 'coastal',
      freshwaterSignal,
      coastalSignal,
      offshoreSignal,
      nearCoastSignal,
      knownFreshwaterAnchor,
      knownCoastalAnchor,
      haystack,
      reason: knownCoastalAnchor ? 'known coastal anchor' : 'coastal text signal',
    };
  }

  // Coordinate proximity alone is not enough to call water saltwater. Broad
  // coast boxes are useful as a weak hint, but rivers, canals and reservoirs can
  // sit well inside those boxes. Leave the realm unknown until text, a known
  // anchor, or exact marine Water Intel supplies stronger evidence.
  if (nearCoastSignal) {
    return {
      contextClass: 'unknown',
      freshwaterSignal,
      coastalSignal,
      offshoreSignal,
      nearCoastSignal,
      knownFreshwaterAnchor,
      knownCoastalAnchor,
      haystack,
      reason: 'near-coast coordinate hint only',
    };
  }

  return {
    contextClass: 'unknown',
    freshwaterSignal,
    coastalSignal,
    offshoreSignal,
    nearCoastSignal,
    knownFreshwaterAnchor,
    knownCoastalAnchor,
    haystack,
    reason: 'no strong water-context signal',
  };
}

export function buildFishIQWaterDebug(input: FishIQWaterContextInput & {
  selectedSpeciesCount?: number | null;
  topRankingReasons?: string[];
  region?: string | null;
}) {
  const resolved = resolveWaterContext(input);
  return {
    coords: {
      lat: typeof input.lat === 'number' ? input.lat : input.lat == null ? null : Number(input.lat),
      lon: typeof input.lon === 'number' ? input.lon : input.lon == null ? null : Number(input.lon),
    },
    region: input.region || input.regionHint || null,
    waterName: input.waterName || null,
    waterType: input.waterType || null,
    waterKind: input.waterKind || null,
    contextClass: resolved.contextClass,
    freshwaterSignal: resolved.freshwaterSignal,
    coastalSignal: resolved.coastalSignal,
    nearCoastSignal: resolved.nearCoastSignal,
    selectedSpeciesCount: input.selectedSpeciesCount ?? null,
    topRankingReasons: input.topRankingReasons || [],
    reason: resolved.reason,
  };
}

export function buildFishIQReadConfidence(input: FishIQWaterContextInput & {
  selectedSpeciesCount?: number | null;
  hasRegionalMatch?: boolean;
  hasHabitatMatch?: boolean;
  fallbackUsed?: boolean;
}) {
  const resolved = resolveWaterContext(input);
  const selectedSpeciesCount = Number(input.selectedSpeciesCount || 0);
  const reasons: string[] = [];
  let score = 0;

  if (input.hasRegionalMatch) {
    score += 2;
    reasons.push('region match');
  }
  if (resolved.contextClass !== 'unknown') {
    score += 2;
    reasons.push('water context match');
  }
  if (input.hasHabitatMatch) {
    score += 1;
    reasons.push('habitat match');
  }
  if (selectedSpeciesCount >= 8) {
    score += 1;
    reasons.push('healthy species set');
  }
  if (input.fallbackUsed) {
    score -= 1;
    reasons.push('fallback used');
  }

  const confidence = score >= 5 ? 'high' : score >= 3 ? 'medium' : 'low';

  return {
    confidence: confidence as 'low' | 'medium' | 'high',
    reasons,
    regionMatch: Boolean(input.hasRegionalMatch),
    waterContextMatch: resolved.contextClass !== 'unknown',
    habitatMatch: Boolean(input.hasHabitatMatch),
    fallbackUsed: Boolean(input.fallbackUsed),
    waterContextReason: resolved.reason,
  };
}
